import { useState, useMemo, useEffect, memo } from "react";
import { Link, useSearchParams, useParams } from "react-router-dom";
import Cookies from 'js-cookie';
import Header from "@/components/layout/Header";

import Footer from "@/components/layout/Footer";
import GameGrid from "@/components/GameGrid";
import TopGamesCarousel from "@/components/TopGamesCarousel";

const MemoizedTopGamesCarousel = memo(TopGamesCarousel);
import HeroSection from "@/components/HeroSection";

import NewsStrip from "@/components/NewsStrip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, XCircle, SlidersHorizontal } from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import SiteUpdateModal from "@/components/SiteUpdateModal";
import RecentCommentsPanel from "@/components/RecentCommentsPanel";
import CommunityHub from "@/components/CommunityHub";
import PollWidget from "@/components/PollWidget";


interface Genre {
  id: number;
  name: string;
  adult?: boolean;
}

interface Tag {
  id: number;
  name: string; // This is what the games API returns
  name_hu?: string;
  name_en?: string;
  adult?: boolean;
}

interface ApiGame {
  id: number;
  name: string;
  description: string | null;
  image_url: string;
  genres: Genre[];
  tags: Array<Tag & { adult?: boolean }>;
  likeCount: number;
  downloadCount: number;
  commentCount: number;
  hasYouTubeVideo: boolean;
  pegiRating: number;
  translationCount: number;
  totalUploadCount: number;
  isAdult: boolean;
  releaseDate: string;
  slug: string;
  hasOfficialTranslation: boolean;
  uploads: number;
  steamLink?: string;
  xxx?: boolean;
}

interface Game {
  id: number;
  image: string;
  name: string;
  description: string;
  slug: string;
  genres: Genre[];
  tags: Tag[];
  likes: number;
  downloads: number;
  comments: number;
  uploads: number;
  translationCount: number;
  totalUploadCount: number;
  hasYouTubeVideo: boolean;
  pegiRating?: string;
  isAgeRestricted?: boolean;
  hasOfficialTranslation?: boolean;
  steamLink?: string;
}

interface AdItem {
  type: 'ad';
  id: string;
}

interface ApiGenre {
  id: number;
  name_en: string;
  name_hu: string | null;
  adult: boolean;
}

const GAMES_PER_PAGE = 9;

const getEditionName = (editionId: number | string): string => {
  switch (String(editionId)) {
    case '0':
      return 'Digital Deluxe';
    case '1':
      return 'Standard';
    case '8':
      return 'Deluxe';
    default:
      return String(editionId);
  }
};

const Index: React.FC = () => {
  const { date: routeDate } = useParams<{ date: string }>();
  const { searchQuery, showAllAdultContent, toggleAllAdultContent } = useAppContext();
  const { currentUser, isAdmin, isTranslator, showShoppingOffers } = useAuth();
  const [pendingSubmissions, setPendingSubmissions] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();

  const [displayItems, setDisplayItems] = useState<(Game | AdItem)[]>([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [errorGames, setErrorGames] = useState<string | null>(null);

  const [genres, setGenres] = useState<ApiGenre[]>([]);
  const [loadingGenres, setLoadingGenres] = useState(true);
  const [errorGenres, setErrorGenres] = useState<string | null>(null);

  // State for all tags
  const [allTags, setAllTags] = useState<{ id: number; name: string }[]>([]);
  const [loadingTags, setLoadingTags] = useState(true);
  const [errorTags, setErrorTags] = useState<string | null>(null);

  // ⬇ URL-ből jövő alapérték
  const genreFromUrl = searchParams.get("genre") || "0";
  const tagFromUrl = searchParams.get("tag") || "0";
  const [selectedGenre, setSelectedGenre] = useState<string>(genreFromUrl);
  const [selectedTag, setSelectedTag] = useState<string>(tagFromUrl);

  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [showWarningNews, setShowWarningNews] = useState(false);
  const [showInfoNews, setShowInfoNews] = useState(false);
  const [showEmailNameWarning, setShowEmailNameWarning] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // State for recommendations
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(true);

  // State for Site Update Modal
  const [siteUpdate, setSiteUpdate] = useState<{ id: number; title: string; content: string } | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);



  // ⬇ URL query figyelése, az oldal betöltéséhez
  useEffect(() => {
    const genreParam = searchParams.get("genre") || "0";
    const tagParam = searchParams.get("tag") || "0";
    const pageParam = parseInt(searchParams.get("page") || "1");

    // Szűrők frissítése az URL-ből
    setSelectedGenre(genreParam);
    setSelectedTag(tagParam);

    // Oldal frissítése az URL-ből
    if (pageParam !== currentPage) {
      setCurrentPage(pageParam);
    }

    // Ha az oldal 1, reseteljük az items-eket
    if (pageParam === 1) {
      setDisplayItems([]);
      setHasMore(true);
    }

    // Betöltés
    loadGames(pageParam, genreParam, tagParam, routeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, routeDate]);

  // ⬇ Keresés és felnőtt tartalom paraméterek változására reagálva
  useEffect(() => {
    setCurrentPage(1);
    setDisplayItems([]);
    setHasMore(true);
    loadGames(1, selectedGenre, selectedTag, routeDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, showAllAdultContent, selectedLetter, routeDate]);

  useEffect(() => {
    const fetchActiveUpdate = async () => {
      try {
        const response = await fetch('/api/site_updates/active');
        if (response.ok) {
          const data = await response.json();
          if (data) {
            const cookieName = `site-update-seen-${data.id}`;
            if (!Cookies.get(cookieName)) {
              setSiteUpdate(data);
              setIsUpdateModalOpen(true);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch site update:", error);
      }
    };
    fetchActiveUpdate();
  }, []);

  useEffect(() => {
    const fetchRecommendations = async () => {
      setLoadingRecommendations(true);
      try {
        const response = await fetch('/api/recommendations/featured');
        if (response.ok) {
          const data = await response.json();
          // Randomize the order using Fisher-Yates shuffle
          const shuffled = [...data];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          setRecommendations(shuffled);
        }
      } catch (error) {
        console.error("Failed to fetch recommendations:", error);
      } finally {
        setLoadingRecommendations(false);
      }
    };
    fetchRecommendations();
  }, []);

  useEffect(() => {
    const fetchGenres = async () => {
      setLoadingGenres(true);
      setErrorGenres(null);
      try {
        const response = await fetch('/api/genres?include_adult=true');
        if (!response.ok) throw new Error('Hiba a műfajok betöltése közben.');
        const data: ApiGenre[] = await response.json();
        setGenres(data);
      } catch (error) {
        setErrorGenres("A műfajok betöltése sikertelen.");
      } finally {
        setLoadingGenres(false);
      }
    };
    fetchGenres();
  }, []);

  useEffect(() => {
    const fetchTags = async () => {
      setLoadingTags(true);
      setErrorTags(null);
      try {
        const response = await fetch('/api/tags');
        if (!response.ok) throw new Error('Hiba a tagek betöltése közben.');
        // The /api/tags endpoint returns { id, name_hu, name_en }
        const data = await response.json();
        setAllTags(data.map((tag: any) => ({ id: tag.id, name: tag.name_hu || tag.name_en })));
      } catch (error) {
        setErrorTags("A tagek betöltése sikertelen.");
      } finally {
        setLoadingTags(false);
      }
    };
    fetchTags();
  }, []);

  const loadGames = async (page: number, genre: string, tag: string, date?: string) => {
    setLoadingGames(true);
    setErrorGames(null);
    try {
      // Ha van dátumszűrés (dailygames), akkor az összes játékot betöltjük
      const pageToUse = date ? 1 : page;
      const limitToUse = date ? 10000 : GAMES_PER_PAGE;

      const params = new URLSearchParams({
        page: String(pageToUse),
        limit: String(limitToUse),
      });

      if (genre !== "0") {
        params.append("genre", genre);
      }
      if (tag !== "0") {
        params.append("tag", tag);
      }
      if (date) {
        params.append("date", date);
      }
      if (selectedLetter) {
        params.append("letter", selectedLetter);
      }
      if (searchQuery.length >= 3) {
        params.append("search", searchQuery);
      }
      if (showAllAdultContent) {
        params.append("show_adult", "true");
      }

      const url = `/api/games?${params.toString()}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Hálózati hiba");
      const data = await response.json();

      const games: Game[] = data.games
        .map((g: ApiGame) => {
          // Ensure all tags have the correct `adult` property
          const tagsWithAdult = g.tags.map(tag => ({
            ...tag,
            adult: tag.adult || false, // Default to false if undefined
          }));

          // Check if game has any adult tags
          const hasAdultTag = tagsWithAdult.some(tag => tag.adult);
          const isAgeRestricted = g.isAdult || hasAdultTag;

          return {
            id: g.id,
            name: g.name,
            slug: g.slug,
            description: g.description || "",
            image: g.image_url,
            genres: g.genres,
            tags: tagsWithAdult,
            likes: g.likeCount,
            downloads: g.downloadCount,
            comments: g.commentCount,
            hasYouTubeVideo: g.hasYouTubeVideo,
            pegiRating: g.pegiRating > 0 ? g.pegiRating.toString() : undefined,
            isAgeRestricted: isAgeRestricted,
            translationCount: g.translationCount,
            totalUploadCount: g.totalUploadCount,
            releaseDate: g.releaseDate,
            hasOfficialTranslation: g.hasOfficialTranslation,
            steamLink: g.steamLink,
          };
        });

      // Ha van dátumszűrés, nem szükséges az oldalazás
      setHasMore(date ? false : games.length === GAMES_PER_PAGE);
      if (page === 1) {
        setDisplayItems(games);
      } else {
        setDisplayItems((prevGames) => [
          ...prevGames,
          ...games.filter((g) => !prevGames.some((pg) => ('type' in pg && pg.type === 'ad') ? false : pg.id === g.id)),
        ]);
      }
    } catch (error) {
      setErrorGames("Játékok betöltése sikertelen.");
    } finally {
      setLoadingGames(false);
    }
  };

  useEffect(() => {
    if (isTranslator) {
      const fetchSubmissions = async () => {
        try {
          const response = await fetch('/api/admin/community_submissions');
          if (response.ok) {
            const data = await response.json();
            setPendingSubmissions(data.length);
          }
        } catch (error) {
          console.error("Failed to fetch pending submissions:", error);
        }
      };
      fetchSubmissions();
    }
  }, [isTranslator]);

  useEffect(() => {
    if (currentUser?.name && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentUser.name)) {
      setShowEmailNameWarning(true);
    }
  }, [currentUser]);

  const selectedGenreName = useMemo(() => {
    if (selectedGenre === "0") return null;
    const found = genres.find(g => String(g.id) === String(selectedGenre));
    return found?.name_hu || found?.name_en || "";
  }, [selectedGenre, genres]);

  const selectedTagName = useMemo(() => {
    if (selectedTag === "0") return null;
    const foundTag = allTags.find(t => String(t.id) === String(selectedTag));
    return foundTag?.name;
  }, [selectedTag, allTags]);

  const handleLoadMore = () => {
    if (!loadingGames && hasMore) {
      // Reklám/ajánlat beszúrása az AuthContext érték alapján
      // Alapértelmezésben TRUE - csak akkor nem jelenik meg, ha explicit FALSE-ra állította
      const showOffers = showShoppingOffers;

      if (showOffers) {
        const adId = `ad-${currentPage}`;
        setDisplayItems(prev => [...prev, { type: 'ad', id: adId }]);
      }
      // Következő oldal betöltése
      setCurrentPage((prevPage) => {
        const nextPage = prevPage + 1;
        // URL frissítése az új oldal számával
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("page", String(nextPage));
        setSearchParams(newSearchParams);
        return nextPage;
      });
    }
  };

  const alphabet = useMemo(() => {
    return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
  }, []);

  const handleFilterChange = (type: 'genre' | 'tag', value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (type === 'genre') {
      setSelectedGenre(value);
      setSelectedTag("0"); // Tag szűrő törlése
      newSearchParams.delete("tag");
      if (value === "0") {
        newSearchParams.delete("genre");
      } else {
        newSearchParams.set("genre", value);
      }
    } else {
      setSelectedTag(value);
      setSelectedGenre("0"); // Műfaj szűrő törlése
      newSearchParams.delete("genre");
      if (value === "0") {
        newSearchParams.delete("tag");
      } else {
        newSearchParams.set("tag", value);
      }
    }

    newSearchParams.delete("page"); // Az oldal paramétert törlünk, hogy az 1. oldalról induljon
    setSearchParams(newSearchParams, { replace: true });
    setCurrentPage(1); // Vissza az első oldalra
  };

  const handleCloseUpdateModal = () => {
    if (siteUpdate) {
      const cookieName = `site-update-seen-${siteUpdate.id}`;
      Cookies.set(cookieName, 'true', { expires: 365 }); // Cookie expires in 1 year
      setIsUpdateModalOpen(false);
    }
  };

  return (
    <>
      <div className="sticky top-0 z-50 w-full">
        <Header />

      </div>
      <main className="flex-grow container mx-auto py-8 main-content-bg  mt-[12px]">
        {isTranslator && pendingSubmissions > 0 && (
          <NewsStrip
            type="info"
            className="mb-6"
            message={`${pendingSubmissions} új közösségi beküldés vár ellenőrzésre. `}
          />
        )}
        {isTranslator && pendingSubmissions > 0 && (
          <div className="mb-6">
            <Link to="/admin/community-submissions" className="font-bold underline text-info">
              Ugrás az ellenőrzéshez
            </Link>
          </div>
        )}
        {showWarningNews && (
          <NewsStrip
            type="warning"
            onClose={() => setShowWarningNews(false)}
            className="mb-6"
            message="Figyelem! Az oldalon jelenleg nem müködik a jelszó újra generálás! Javítás alatt :) sorry."
          />
        )}
        {showInfoNews && !currentUser && (
          <NewsStrip
            type="info"
            onClose={() => setShowInfoNews(false)}
            className="mb-6"
            message="Üdvözlünk! A belépési hiba elhárult. Ha mégsem tudsz belépni, kérj új jelszót 🙂"
          />
        )}
        {showEmailNameWarning && (
          <NewsStrip
            type="warning"
            onClose={() => setShowEmailNameWarning(false)}
            className="mb-6"
            message="Az e-mail cím nevedként van megadva, kérlek frissítsd a profilodban!"
          />
        )}

        {siteUpdate && (
          <SiteUpdateModal
            isOpen={isUpdateModalOpen}
            onClose={handleCloseUpdateModal}
            title={siteUpdate.title}
            content={siteUpdate.content}
          />
        )}

        {!routeDate && <HeroSection />}
        {!routeDate && showShoppingOffers && (
          <div className="my-8 px-4">
            <h2 className="text-3xl font-bold text-center mb-4 text-primary">Instant Gaming ajánlatok</h2>
            {loadingRecommendations ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
                <p className="text-muted-foreground">Ajánlatok betöltése...</p>
              </div>
            ) : recommendations.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
                <p className="text-muted-foreground">Jelenleg nincsenek ajánlatott játékok.</p>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 overflow-hidden flex flex-col">
                <div className="overflow-x-auto overflow-y-auto max-h-[195px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-800">
                      <tr className="border-b border-gray-600">
                        <th className="text-left py-3 px-4 font-semibold">Játék</th>
                        <th className="text-left py-3 px-4 font-semibold">Ár</th>
                        <th className="text-left py-3 px-4 font-semibold">Platform</th>
                        <th className="text-left py-3 px-4 font-semibold">Kategória</th>
                        <th className="text-left py-3 px-4 font-semibold">Edition</th>
                        <th className="text-left py-3 px-4 font-semibold">Régió</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendations.map((game) => (
                        <tr key={game.ig_id} className="border-b border-gray-700 hover:bg-gray-700/50 transition-colors">
                          <td className="py-3 px-4 font-medium">
                            {game.url ? (
                              <button
                                onClick={() => window.open(`${game.url.split('?')[0]}?igr=gamer-32cbcdb`, '_blank')}
                                className="text-primary hover:text-primary-foreground transition-colors cursor-pointer text-left"
                              >
                                {game.name}
                              </button>
                            ) : (
                              game.name
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {game.retail && parseFloat(game.retail) !== parseFloat(game.price) && (
                              <s className="text-muted-foreground mr-1">${typeof game.retail === 'string' ? parseFloat(game.retail).toFixed(2) : game.retail.toFixed(2)}</s>
                            )}
                            <span>${typeof game.price === 'string' ? parseFloat(game.price).toFixed(2) : game.price.toFixed(2)}</span>
                          </td>
                          <td className="py-3 px-4">{game.systems || '-'}</td>
                          <td className="py-3 px-4">{game.category || '-'}</td>
                          <td className="py-3 px-4">{game.edition_id ? getEditionName(game.edition_id) : '-'}</td>
                          <td className="py-3 px-4">{game.region || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Community Hub Section */}
        {!routeDate && (
          <div className="my-8">
            <CommunityHub />
          </div>
        )}

        {/* TOP Games Carousel */}
        {!routeDate && selectedGenre === "0" && selectedTag === "0" && <MemoizedTopGamesCarousel />}

        {!routeDate && (
          <div className="mb-8 px-4">
            <Button
              onClick={() => setShowFilters(prev => !prev)}
              variant="outline"
              className="w-full bg-card text-card-foreground border-border hover:bg-secondary hover:text-secondary-foreground"
            >
              <SlidersHorizontal size={16} className="mr-2" />
              Szűrők {showFilters ? 'elrejtése' : 'mutatása'}
            </Button>

            {showFilters && (
              <div className="flex flex-col gap-4 mt-4 p-4 bg-card border border-border rounded-lg"> {/* Main container for filter rows */}
                {/* First row: Genre, Tag, Adult Content Toggle */}
                <div className="flex flex-wrap justify-center items-center gap-4 w-full">
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Select onValueChange={(value) => handleFilterChange('genre', value)} value={selectedGenre} disabled={loadingGenres}>
                      <SelectTrigger className={`w-full sm:w-[180px] bg-gray-800 text-secondary-foreground focus:ring-primary ${selectedGenre !== "0" ? "border-2 border-secondary-foreground" : "border border-border"}`}>
                        <SelectValue placeholder="Műfaj szűrése" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 text-card-foreground border-border">
                        {loadingGenres ? (
                          <SelectItem value="loading" disabled>Betöltés...</SelectItem>
                        ) : errorGenres ? (
                          <SelectItem value="error" disabled>{errorGenres}</SelectItem>
                        ) : (
                          <>
                            <SelectItem value="0">Összes műfaj</SelectItem>
                            {genres.map((genre) => (
                              <SelectItem key={genre.id} value={String(genre.id)}>
                                {genre.name_hu || genre.name_en}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedGenre !== "0" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleFilterChange('genre', '0')}
                        title="Műfaj szűrő törlése"
                      >
                        <XCircle className="h-5 w-5 text-destructive" />
                      </Button>
                    )}
                  </div>

                  {/* Tag Filter */}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Select onValueChange={(value) => handleFilterChange('tag', value)} value={selectedTag} disabled={loadingTags}>
                      <SelectTrigger className={`w-full sm:w-[180px] bg-gray-800 text-secondary-foreground focus:ring-primary ${selectedTag !== "0" ? "border-2 border-secondary-foreground" : "border border-border"}`}>
                        <SelectValue placeholder="Tag szűrése" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 text-card-foreground border-border">
                        {loadingTags ? (
                          <SelectItem value="loading" disabled>Betöltés...</SelectItem>
                        ) : errorTags ? (
                          <SelectItem value="error" disabled>{errorTags}</SelectItem>
                        ) : (
                          <><SelectItem value="0">Összes tag</SelectItem>
                            {allTags.map(tag => (<SelectItem key={tag.id} value={String(tag.id)}>{tag.name}</SelectItem>))}</>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedTag !== "0" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleFilterChange('tag', '0')}
                        title="Tag szűrő törlése"
                      >
                        <XCircle className="h-5 w-5 text-destructive" />
                      </Button>
                    )}
                  </div>


                </div>

                {/* Second row: Alphabetical Filter */}
                <div className="flex flex-wrap justify-center gap-1 w-full flex-grow mt-4">
                  <Button variant={selectedLetter === null ? "default" : "outline"} onClick={() => setSelectedLetter(null)} className={`h-auto px-2 py-1 text-xs ${selectedLetter === null ? "bg-primary text-primary-foreground" : "bg-gray-800 text-secondary-foreground border-border"} hover:border-primary hover:bg-muted hover:text-muted-foreground transition-colors duration-200 cursor-pointer`}>Összes</Button>
                  {alphabet.map((letter) => (
                    <Button key={letter} variant={selectedLetter === letter ? "default" : "outline"} onClick={() => setSelectedLetter(letter)} className={`h-auto px-2 py-1 text-xs ${selectedLetter === letter ? "bg-primary text-primary-foreground" : "bg-gray-800 text-secondary-foreground border-border"} hover:border-primary hover:bg-muted hover:text-muted-foreground transition-colors duration-200 cursor-pointer`}>{letter}</Button>
                  ))}
                </div>
              </div>
            )}
          </div >
        )}

        {/* Figyelmeztetés ha genre aktív */}
        {
          (selectedGenre !== "0" || selectedTag !== "0") && (
            <NewsStrip
              type="info"
              className="mb-6"
              message={`A játékok szűrve vannak: ${selectedGenre !== "0" ? `műfaj: "${selectedGenreName}"` : ''}${selectedGenre !== "0" && selectedTag !== "0" ? ' és ' : ''}${selectedTag !== "0" ? `tag: "${selectedTagName || selectedTag}"` : ''}. A szűrés törléséhez válaszd az "Összes" opciót, vagy kattints a mellette lévő X gombra.`}
            />
          )
        }

        {
          routeDate && (
            <NewsStrip
              type="info"
              className="mb-6"
              message={`Napi feltöltések mutatása a következő dátumhoz: ${routeDate}`}
            />
          )
        }

        {
          errorGames && (
            <div className="text-center text-destructive text-xl py-8">{errorGames}</div>
          )
        }



        {/* Wrapper to prevent Google Auto Ads from inserting ads before the "Load More" button and to group loading elements */}
        <div data-nosnippet data-ad-suppress="true">
          <GameGrid items={displayItems} onGenreClick={(id) => handleFilterChange('genre', id)} onTagClick={(id) => handleFilterChange('tag', id)} />

          {loadingGames && (
            <div className="text-center text-primary text-xl py-8">Játékok betöltése...</div>
          )}

          {!loadingGames && hasMore && (
            <>

              <div className="flex justify-center mt-8">
                <Button onClick={handleLoadMore} variant="default">
                  További játékok betöltése
                </Button>
              </div>

            </>
          )}
        </div>
      </main >
      <Footer />
    </>
  );
};

export default Index;

